'use client';

import { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { slugify } from '@util/urlHelpers';
import { CategoryDocument } from 'models/category';
import { RideType, UserBike } from 'models/shared';
import { UserSizes } from 'models/user';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { isMobile } from '..';
import { PageSideFilters } from './useProductsPage';

export interface FormattedFacetCount {
  value: string;
  count: number;
  order: string;
}

export const useCategoryFilters = ({
  filterState,
  setFilterState,
}: {
  filterState: CategoryPageSideFilters;
  setFilterState: (filterState: CategoryPageSideFilters) => void;
}) => {
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [aspectRatioFilter, setAspectRatioFilter] = useState<string>('');
  const pathname = usePathname();
  const [startAllClosed, setStartAllClosed] = useState(false);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const [activePage, setActivePage] = useState<
    | 'main'
    | 'categories'
    | 'brands'
    | 'price'
    | 'shipsTo'
    | 'shipsFrom'
    | 'bootSizes'
    | 'pantSizes'
    | 'letterSizes'
    | 'rideTypes'
  >('main');

  // pending filters are the accumulated changes from the mobile filters, they are all applied at once by the applyPendingFilterChanges function
  // we only use the pending filters on mobile because the desktop doesn't have an apply button
  const [pendingFilterChanges, setPendingFilterChanges] = useState<
    Partial<PageSideFilters>
  >({});

  const handleFilterChange = (key: string, value: any) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        [key]: value,
      }));
    } else {
      setFilterState({
        ...filterState,
        [key]: value,
      });
    }
  };

  const handlePriceRangeChange = (min: number | null, max: number | null) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        minPrice: min,
        maxPrice: max,
      }));
    } else {
      setFilterState({
        ...filterState,
        minPrice: min,
        maxPrice: max,
      });
    }
  };

  const handleBikeSelection = ({
    bike,
    bikes,
  }: {
    bike?: Partial<UserBike> | null;
    bikes?: Partial<UserBike>[];
  }) => {
    if (isMobile()) {
      if (bikes && Array.isArray(bikes)) {
        setPendingFilterChanges((prev) => ({
          ...filterState,
          ...prev,
          bikeYears: bikes.map((b) => `${b.year}`),
          bikeMakes: bikes.map((b) => b.make!),
          bikeModels: bikes.map((b) => b.model!),
        }));
        return;
      }
      if (bike === null) {
        setPendingFilterChanges((prev) => ({
          ...filterState,
          ...prev,
          bikeYears: [],
          bikeMakes: [],
          bikeModels: [],
        }));
        return;
      }
      if (bike) {
        setPendingFilterChanges((prev) => ({
          ...filterState,
          ...prev,
          bikeYears: [
            ...(prev?.bikeYears ?? filterState.bikeYears ?? []),
            `${bike.year}`,
          ],
          bikeMakes: [
            ...(prev?.bikeMakes ?? filterState.bikeMakes ?? []),
            bike.make!,
          ],
          bikeModels: [
            ...(prev?.bikeModels ?? filterState.bikeModels ?? []),
            bike.model!,
          ],
        }));
      }
    } else {
      if (bikes && Array.isArray(bikes)) {
        setFilterState({
          ...filterState,
          bikeYears: bikes.map((b) => `${b.year}`),
          bikeMakes: bikes.map((b) => b.make!),
          bikeModels: bikes.map((b) => b.model!),
          rideTypes: bikes.map((b) => b.type!),
        });
        return;
      }
      if (bike === null) {
        setFilterState({
          ...filterState,
          bikeYears: [],
          bikeMakes: [],
          bikeModels: [],
          rideTypes: [],
        });
        return;
      }
      if (bike) {
        setFilterState({
          ...filterState,
          bikeYears: [...(filterState.bikeYears ?? []), `${bike.year}`],
          bikeMakes: [...(filterState.bikeMakes ?? []), bike.make!],
          bikeModels: [...(filterState.bikeModels ?? []), bike.model!],
          rideTypes: [...(filterState.rideTypes ?? []), bike.type!],
        });
      }
    }
  };

  const handleSizeChange = (sizes: UserSizes) => {
    const letterSizes = Object.values(sizes).filter(
      (size) => typeof size === 'string'
    );
    const numberSizes = Object.values(sizes)
      .filter((size) => typeof size === 'number')
      .map(String);

    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        letterSizes,
        numberSizes,
      }));
    } else {
      setFilterState({
        ...filterState,
        letterSizes,
        numberSizes,
      });
    }
  };

  const handleCategoryChange = (
    product_cat: string,
    level: 'ride_type' | 'category' | 'category1' | 'category2' | 'category3'
  ) => {
    const newFilters = {
      ...filterState,
      product_cats: {
        ...filterState.product_cats,
        [level]: product_cat,
      },
    };

    if (isMobile()) {
      const newPendingFilterChanges = {
        ...pendingFilterChanges,
        ...newFilters,
      };

      setPendingFilterChanges(newPendingFilterChanges);
    } else {
      setFilterState(newFilters);
    }
  };

  const handleRideTypeChange = (filters: RideType[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        rideTypes: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        rideTypes: filters,
      });
    }
  };
  const handleFreeShippingChange = (is_flat_rate: boolean) => {
    const newFilters = {
      ...filterState,
      is_flat_rate,
    };

    if (isMobile()) {
      const newPendingFilterChanges = {
        ...pendingFilterChanges,
        ...newFilters,
      };

      setPendingFilterChanges(newPendingFilterChanges);
    } else {
      setFilterState(newFilters);
    }
  };

  const applyPendingFilterChanges = () => {
    if (pendingFilterChanges) {
      setFilterState({
        ...filterState,
        ...pendingFilterChanges,
      });
    }
  };

  const getCatUrl = (
    filters: PageSideFilters,
    current_cat: CategoryDocument
  ) => {
    if (!pathname) return '';

    let base_path = current_cat.full_slug.split('/').slice(0, -1).join('/');
    let end_path = current_cat.full_slug.split('/').slice(-1)[0];

    const urlAdditions = [];
    if (filters.conditions && filters.conditions.length === 1) {
      // technically conditions can only be 1 cause its a radio but just in case
      let condition = filters.conditions[0];
      urlAdditions.push(condition.toLowerCase());
    }
    if (filters.genders) {
      filters.genders.forEach((gender) => {
        const urlGenders = {
          Y: 'youth',
          W: 'women',
          M: 'men',
          U: 'unisex',
        };
        urlAdditions.push(urlGenders[gender].toLowerCase());
      });
    }
    if (filters.numberSizes) {
      filters.numberSizes.forEach((size) => {
        urlAdditions.push(`size-${size.replace('.', '-')}`);
      });
    }
    if (filters.letterSizes) {
      filters.letterSizes.forEach((size) => {
        urlAdditions.push(`size-${size}`);
      });
    }
    if (filters.bikeYears?.length) {
      filters.bikeYears.forEach((year) => {
        urlAdditions.push(slugify(`${year}`));
      });
    }
    if (filters.bikeMakes?.length) {
      filters.bikeMakes.forEach((make) => {
        urlAdditions.push(slugify(make));
      });
    }
    if (filters.bikeModels) {
      filters.bikeModels.forEach((model) => {
        urlAdditions.push(slugify(model));
      });
    }
    if (!current_cat?.is_master_product && filters.brands?.length) {
      let brand = filters.brands[0];
      if (brand) {
        brand = brand.replace('%', '-percent').replace(' ', '-');
        brand = brand.replace('&', '');
        brand = brand.replace(`'`, '').replace(')', '');
        brand = brand.replace(' ', '-');
        brand = brand.replace('’', '');
        urlAdditions.push(brand.toLowerCase());
      }
    }
    if (filters.onsale) {
      urlAdditions.push('on-sale');
    }
    let path = `${base_path}`;
    let additions_path = urlAdditions.join('-');
    if (additions_path.endsWith('-')) {
      additions_path = additions_path.slice(0, -1);
    }
    if (additions_path.length > 0) {
      if (end_path === 'search') {
        path += `/search/${additions_path}`;
      } else {
        path += `/${additions_path}-${end_path}`;
      }
    } else {
      path += `/${end_path}`;
    }
    if (path.endsWith('-')) {
      path = path.slice(0, -1);
    }
    return path;
  };

  // const formattedFacetCounts = formatFacetCounts();

  const [previousFilterState, setPreviousFilterState] =
    useState<PageSideFilters>(filterState);
  const onResetFilters = () => {
    setPreviousFilterState(filterState);
    setPendingFilterChanges({
      conditions: [],
      sellerType: [],
      brands: [],
      numberSizes: [],
      letterSizes: [],
      rideTypes: [],
      minPrice: null,
      maxPrice: null,
      onsale: false,
      recentlySold: false,
      bikeYears: [],
      bikeMakes: [],
      bikeModels: [],
    });
  };
  const onDismiss = () => {
    setPendingFilterChanges({
      ...previousFilterState,
      ...pendingFilterChanges,
    });
  };

  return {
    handleFreeShippingChange,
    handleRideTypeChange,
    brandFilter,
    setBrandFilter,
    aspectRatioFilter,
    setAspectRatioFilter,
    getCatUrl,
    startAllClosed,
    setStartAllClosed,
    showMobileDrawer,
    setShowMobileDrawer,
    onResetFilters,
    pendingFilterChanges,
    setPendingFilterChanges,
    handleBikeSelection,
    handleSizeChange,
    // NEW FUNCTIONS:
    handleFilterChange,
    handleCategoryChange,
    applyPendingFilterChanges,
    handlePriceRangeChange,
    activePage,
    setActivePage,
    onDismiss,
  };
};
